import { Controller } from "@hotwired/stimulus"
import moment from "moment"

moment.locale('ja')

const WEEKDAY_NAMES = ['日', '月', '火', '水', '木', '金', '土']

export default class extends Controller {
  static targets = ["weekContainer", "selectedList", "submitButton", "countMessage", "hiddenFields"]
  static values = {
    candidates: Array,
    selectedDateTimes: Array,
    minCount: Number,
    // 登録済みの希望時間帯。追加登録時に選択できないよう表示する [{ first_time, last_time }]
    registeredDurations: { type: Array, default: [] },
    // 選択件数のメッセージに表示する送信ボタン名
    submitLabel: { type: String, default: 'この希望日程で申し込み完了' },
    noWeekdays: { type: Boolean, default: false }
  }

  connect() {
    this.selectedSlots = []
    this.weekOffset = 0

    // 既選択の時間帯を復元
    this.selectedDateTimesValue.forEach(dt => {
      const m = moment(dt)
      const date = m.format('YYYY-MM-DD')
      const candidate = this.candidatesValue.find(c => c.date === date)
      const durationHours = candidate ? candidate.duration_hours : 1
      this.selectedSlots.push({
        date,
        startTime: m.format('HH:mm'),
        endTime: m.clone().add(durationHours, 'hours').format('HH:mm'),
        firstTime: m.format('YYYY-MM-DD HH:mm:ss'),
        lastTime: m.clone().add(durationHours, 'hours').format('YYYY-MM-DD HH:mm:ss')
      })
    })

    this.renderWeek()
    this.updateUI()
  }

  toggleNoWeekdays() {
    this.noWeekdaysValue = !this.noWeekdaysValue
    // 平日除外に切り替えたら平日の選択を解除
    if (this.noWeekdaysValue) {
      this.selectedSlots = this.selectedSlots.filter(slot => {
        const candidate = this.candidatesValue.find(c => c.date === slot.date)
        return candidate && candidate.weekend
      })
    }
    this.renderWeek()
    this.updateUI()
  }

  prevWeek() {
    if (this.weekOffset > 0) {
      this.weekOffset--
      this.renderWeek()
    }
  }

  nextWeek() {
    const maxWeeks = Math.ceil(this.candidatesValue.length / 7)
    if (this.weekOffset < maxWeeks - 1) {
      this.weekOffset++
      this.renderWeek()
    }
  }

  toggleSlot(event) {
    event.stopPropagation()
    const el = event.currentTarget
    const date = el.dataset.date
    const startTime = el.dataset.startTime
    const endTime = el.dataset.endTime
    const firstTime = el.dataset.firstTime
    const lastTime = el.dataset.lastTime

    const existingIndex = this.selectedSlots.findIndex(
      s => s.date === date && s.startTime === startTime
    )

    if (existingIndex >= 0) {
      this.selectedSlots.splice(existingIndex, 1)
    } else {
      if (this.isOverlapping(date, startTime, endTime)) return
      this.selectedSlots.push({ date, startTime, endTime, firstTime, lastTime })
    }

    this.renderWeek()
    this.updateUI()
  }

  removeSlot(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    this.selectedSlots.splice(index, 1)
    this.renderWeek()
    this.updateUI()
  }

  // private

  isOverlapping(date, startTime, endTime) {
    return this.selectedSlots.some(slot => {
      if (slot.date !== date) return false
      return (startTime < slot.endTime && endTime > slot.startTime)
    })
  }

  registeredSlots() {
    if (!this._registeredSlots) {
      this._registeredSlots = this.registeredDurationsValue.map(d => {
        const first = moment(d.first_time)
        const last = moment(d.last_time)
        return { date: first.format('YYYY-MM-DD'), startTime: first.format('HH:mm'), endTime: last.format('HH:mm') }
      })
    }
    return this._registeredSlots
  }

  registeredDurationAt(date, timeStr) {
    return this.registeredSlots().find(s => s.date === date && timeStr >= s.startTime && timeStr < s.endTime)
  }

  isRegisteredOverlapping(date, startTime, endTime) {
    return this.registeredSlots().some(s => s.date === date && startTime < s.endTime && endTime > s.startTime)
  }

  isSlotSelected(date, startTime) {
    return this.selectedSlots.some(s => s.date === date && s.startTime === startTime)
  }

  currentWeekCandidates() {
    const start = this.weekOffset * 7
    const end = start + 7
    return this.candidatesValue.slice(start, end)
  }

  renderWeek() {
    const candidates = this.currentWeekCandidates()
    if (candidates.length === 0) return

    const maxWeeks = Math.ceil(this.candidatesValue.length / 7)

    // ナビゲーション
    let html = '<div class="interview-calendar__nav">'
    html += `<button type="button" class="interview-calendar__nav-btn" data-action="click->interview-calendar#prevWeek" ${this.weekOffset === 0 ? 'disabled' : ''}><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg></button>`
    html += `<button type="button" class="interview-calendar__nav-btn" data-action="click->interview-calendar#nextWeek" ${this.weekOffset >= maxWeeks - 1 ? 'disabled' : ''}><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 6 15 12 9 18"></polyline></svg></button>`
    html += '</div>'

    // テーブル
    html += '<table class="interview-calendar__table">'

    // ヘッダ行（日付）
    html += '<thead><tr><th></th>'
    candidates.forEach(candidate => {
      const m = moment(candidate.date)
      const dayName = WEEKDAY_NAMES[m.day()]
      const dayClass = candidate.weekend ? (m.day() === 0 ? 'tx--red' : 'tx--blue') : ''
      html += `<th class="${dayClass}">${m.format('M/D')}<br class="interview-calendar__sp-br">(${dayName})</th>`
    })
    html += '</tr></thead>'

    // 時間行（9:00〜19:30、30分間隔）
    const timeSlots = []
    for (let hour = 9; hour <= 19; hour++) {
      timeSlots.push({ hour, minute: 0 })
      timeSlots.push({ hour, minute: 30 })
    }

    html += '<tbody>'
    timeSlots.forEach(({ hour, minute }) => {
      const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
      const isHalfHour = minute === 30

      html += `<tr class="${isHalfHour ? 'interview-calendar__row--half' : ''}">`

      // 時間ラベル: :00 行は rowspan=2、:30 行はスキップ
      if (!isHalfHour) {
        html += `<td class="interview-calendar__hour-label" rowspan="2">${hour}時</td>`
      }

      candidates.forEach(candidate => {
        const isWeekend = candidate.weekend
        const isHoliday = candidate.holiday
        const isNoWeekday = this.noWeekdaysValue && !isWeekend
        const isDisabled = isHoliday || isNoWeekday

        if (isDisabled) {
          const disabledClass = isNoWeekday ? 'interview-calendar__cell--weekday-disabled' : ''
          html += `<td class="interview-calendar__cell interview-calendar__cell--disabled ${disabledClass}"></td>`
          return
        }

        // この時刻が選択済みスロットの範囲内かチェック
        const coveringSlot = this.selectedSlots.find(s =>
          s.date === candidate.date && timeStr >= s.startTime && timeStr < s.endTime
        )

        if (coveringSlot) {
          const isStart = coveringSlot.startTime === timeStr
          if (!isStart) {
            // 継続セル: rowspan でカバー済みなので出力しない
            return
          }
          // 開始セル: rowspan=2 で結合
          html += `<td class="interview-calendar__cell interview-calendar__cell--selected" rowspan="2"
            data-action="click->interview-calendar#toggleSlot"
            data-date="${candidate.date}"
            data-start-time="${coveringSlot.startTime}"
            data-end-time="${coveringSlot.endTime}"
            data-first-time="${coveringSlot.firstTime}"
            data-last-time="${coveringSlot.lastTime}">
            <span>選択中</span>
            <button type="button" class="interview-calendar__cell-remove"
              data-action="click->interview-calendar#toggleSlot"
              data-date="${candidate.date}"
              data-start-time="${coveringSlot.startTime}"
              data-end-time="${coveringSlot.endTime}"
              data-first-time="${coveringSlot.firstTime}"
              data-last-time="${coveringSlot.lastTime}">&times;</button>
            <br><span>${coveringSlot.startTime} - ${coveringSlot.endTime}</span>
          </td>`
          return
        }

        // 登録済みの希望時間帯に含まれる時刻は選択不可
        const registered = this.registeredDurationAt(candidate.date, timeStr)
        if (registered) {
          const label = registered.startTime === timeStr ? '<span>登録済</span>' : ''
          html += `<td class="interview-calendar__cell interview-calendar__cell--disabled interview-calendar__cell--registered">${label}</td>`
          return
        }

        // この時刻の利用可能スロットを探す
        const availableTime = candidate.selectable_start_times.find(t => t === timeStr)

        if (availableTime) {
          const [h, mi] = availableTime.split(':')
          const startMoment = moment(candidate.date).hour(parseInt(h)).minute(parseInt(mi))
          const endMoment = startMoment.clone().add(candidate.duration_hours, 'hours')
          const startTime = startMoment.format('HH:mm')
          const endTime = endMoment.format('HH:mm')
          const firstTime = startMoment.format('YYYY-MM-DD HH:mm:ss')
          const lastTime = endMoment.format('YYYY-MM-DD HH:mm:ss')
          const overlapping = this.isOverlapping(candidate.date, startTime, endTime) || this.isRegisteredOverlapping(candidate.date, startTime, endTime)

          if (overlapping) {
            html += '<td class="interview-calendar__cell interview-calendar__cell--disabled"></td>'
          } else {
            html += `<td class="interview-calendar__cell"
              data-action="click->interview-calendar#toggleSlot"
              data-date="${candidate.date}"
              data-start-time="${startTime}"
              data-end-time="${endTime}"
              data-first-time="${firstTime}"
              data-last-time="${lastTime}"></td>`
          }
        } else {
          html += '<td class="interview-calendar__cell"></td>'
        }
      })

      html += '</tr>'
    })
    html += '</tbody></table>'

    this.weekContainerTarget.innerHTML = html
  }

  updateUI() {
    this.renderSelectedList()
    this.renderCountMessage()
    this.renderHiddenFields()
    this.updateSubmitButton()
  }

  renderSelectedList() {
    const sorted = [...this.selectedSlots].sort((a, b) => a.firstTime.localeCompare(b.firstTime))

    if (sorted.length === 0) {
      this.selectedListTarget.innerHTML = ''
      return
    }

    let html = '<div class="block-sm"><b>選択中の時間帯</b></div>'
    sorted.forEach((slot, index) => {
      const m = moment(slot.firstTime)
      const originalIndex = this.selectedSlots.indexOf(slot)
      html += `<div class="interview-calendar__selected-item">
        ${m.format('YYYY年M月D日')}（${WEEKDAY_NAMES[m.day()]}）${slot.startTime} - ${slot.endTime}
        <button type="button" class="interview-calendar__remove-btn" data-action="click->interview-calendar#removeSlot" data-index="${originalIndex}">&times;</button>
      </div>`
    })

    this.selectedListTarget.innerHTML = html
  }

  renderCountMessage() {
    const remaining = this.minCountValue - this.selectedSlots.length
    if (remaining > 0) {
      this.countMessageTarget.innerHTML = `<div>あと <b class="tx--red tx-xl">${remaining}件 </b>選んでください。</div>`
    } else {
      this.countMessageTarget.innerHTML = `<div>以下の${this.selectedSlots.length}件を選択しています。<br>よろしければ「${this.submitLabelValue}」を押してください。</div>`
    }
  }

  renderHiddenFields() {
    const sorted = [...this.selectedSlots].sort((a, b) => a.firstTime.localeCompare(b.firstTime))
    let html = ''
    sorted.forEach(slot => {
      html += `<input type="hidden" name="candidates[][first_time]" value="${slot.firstTime}">`
      html += `<input type="hidden" name="candidates[][last_time]" value="${slot.lastTime}">`
    })
    this.hiddenFieldsTarget.innerHTML = html
  }

  updateSubmitButton() {
    const passed = this.selectedSlots.length >= this.minCountValue
    this.submitButtonTarget.disabled = !passed
    this.submitButtonTarget.className = passed ? 'btn-emphasis btn--medium' : 'btn-disabled btn--medium'
  }
}
